import React, { useState, useEffect } from "react";
import clsx from "clsx";

import { makeStyles, styled } from "@mui/styles";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

import Typography from "@mui/material/Typography";

import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";

import Grid from "@mui/material/Grid";

import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import MoreIcon from "@mui/icons-material/More";
import { useConfirm } from "material-ui-confirm";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import yellow from "@mui/material/colors/yellow";
import teal from "@mui/material/colors/teal";
import lime from "@mui/material/colors/lime";
import deepPurple from "@mui/material/colors/deepPurple";
import grey from "@mui/material/colors/grey";
import red from "@mui/material/colors/red";
import deepOrange from "@mui/material/colors/deepOrange";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import PrintIcon from "@mui/icons-material/Print";
import LabelIcon from "@mui/icons-material/Label";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import TabletIcon from '@mui/icons-material/Tablet';
import VerticalAlignTopIcon from '@mui/icons-material/VerticalAlignTop';
import TagIcon from "@mui/icons-material/Tag";

import { OFF_SHELF_SOLD_OUT, offShelfLabel } from "util/offShelf";

import { default as MenuItemOptions } from "../MenuItemOptions";

import { default as TargetBox } from "../TargetBox";

import MenuItemImage from "./MenuItemImage";
import { useSelector, useDispatch } from "react-redux";

import { removeItemWithSlice } from "util/utils";
import { getCurrencySymbol } from "util/currencyUtils";
import { minutesToString } from "util/menuUtils";

import { updateMenuItem, deleteMenuPhoto } from "store/actions/restaurantMenu";
import MenuMiscSettings from "../Category/MenuMiscSettings";
import MoveCategoryDialog from "./MoveCategoryDialog";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  details: {
    display: "flex",
    flex: 1,
    flexDirection: "column",
  },
  category: {
    width: "90%",
  },
  content: {
    width: "100%",
  },
  idChip: {
    fontFamily: 'monospace',
    fontSize: '0.7rem',
    height: 20,
    backgroundColor: grey[100],
    border: `1px solid ${grey[300]}`,
    color: grey[600],
    cursor: 'text',
    userSelect: 'all',
  },
  cover: {
    width: 50,
  },
  controls: {
    display: "flex",
    alignItems: "center",
    paddingLeft: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  playIcon: {
    height: 50,
    width: 50,
  },
  expand: {
    transform: "rotate(0deg)",
    marginLeft: "auto",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.shortest,
    }),
  },
  expandOpen: {
    transform: "rotate(180deg)",
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  dropHint: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
  sizeRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    padding: theme.spacing(0.5, 0),
  },
  // Dashed outline marks this as a drop target for choice groups. Keep the
  // affordance obvious — just in theme colours rather than raw green.
  paper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    listStyle: "none",
    padding: theme.spacing(1.5),
    border: `1px dashed ${theme.palette.primary.light}`,
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    marginBottom: ".5rem",
    minHeight: 52,
  },
  placeholder: {
    backgroundColor: "#f7f7f7",
    border: "1px dotted #f7f7f7",
  },
  sizeClass: {
    height: "50",
    width: "100%",
  },
  optionsClass: {
    width: "100%",
  },
  margin: {
    margin: theme.spacing(1),
  },
  marginRightBottom: {
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
}));

const GroupChip = styled(Chip)({
  background: "linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)",

  color: "white",
});

const MenuItemDisplay = (props) => {
  const { menuItem, index, categoryId, menuId, showIds = false, offShelf = null } = props;
  // The live half of `offShelf` comes from `inactiveMenus`, which the POS and the stock counter write
  // to; the visibility chips below it come from this dish's own Firestore flags. They are separate
  // reasons a customer cannot order the dish, so they are labelled separately.
  const liveOffShelf = offShelf ? offShelf.live : null;
  const classes = useStyles();

  const dispatch = useDispatch();
  const [groupList, setGroupList] = useState([]);
  const [sizeList, setSizeList] = useState([]);
  const user = useSelector((state) => state.account.user);
  const currencySymbol = getCurrencySymbol(user);
  const formatPrice = (value) =>
    `${currencySymbol}${Number(value ?? 0).toFixed(2)}`;

  const confirm = useConfirm();
  const [imageUpload, setImageUpload] = useState(false);
  const [openMenOptions, setOpenMenOptions] = useState(false);
  const [openMenuSettings, setOpenMenuSettings] = useState(false);
  const [openMove, setOpenMove] = useState(false);

  const updateTheMenuItem = (item, values) =>
    dispatch(updateMenuItem(item, values));
  const menuImages = useSelector((state) => state.restaurantMenu.menuImages);
  const categories = useSelector(
    (state) => state.restaurantMenu.menuCategories
  );
  const allGroups = useSelector((state) => state.restaurantMenu.menuGroups);

  const deleteMenuImage = (values) => dispatch(deleteMenuPhoto(values));

  const getGroupName = (groupId) => {
    //console.log('allGroups', allGroups);
    const groupIdx = allGroups.findIndex((g) => g.id === groupId);
    if (groupIdx !== -1) {
      return allGroups[groupIdx].name;
    } else {
      return "No name";
    }
  };

  useEffect(() => {
    if (menuItem.groups) {
      setGroupList(menuItem.groups);
    }

    if (menuItem.sizes) {
      setSizeList(menuItem.sizes);
    }
  }, [menuItem.groups]);

  useEffect(() => {
    if (menuItem.sizes) {
      setSizeList(menuItem.sizes);
    }
  }, [menuItem.sizes]);

  const handleDelete = (chipToDelete) => () => {
    console.log("handle group Delete");

    const newGroups = menuItem.groups.filter(
      (group) => group.id !== chipToDelete.id
    );
    updateTheMenuItem({ ...menuItem, groups: newGroups, menuId }, categories);
  };

  const moveGroup = (dragIndex, hoverIndex) => {
    console.log("groups", menuItem.groups, dragIndex, hoverIndex);
    let tempGroups = [...menuItem.groups];
    [tempGroups[dragIndex], tempGroups[hoverIndex]] = [
      tempGroups[hoverIndex],
      tempGroups[dragIndex],
    ];
    const newGroups = tempGroups.map((group, sortIndex) => {
      return { ...group, sort: sortIndex };
    });
    props.moveGroup(index, newGroups);
  };

  const deleteImage = () => {
    //console.log('deleteImage', menuId, categoryId, menuItem.id);
    deleteMenuImage({ menuId, categoryId, menuItemId: menuItem.id });
  };

  const openImageUpload = () => {
    setImageUpload(true);
  };

  const openMenuOptions = () => {
    setOpenMenOptions(true);
  };

  const closeImageUpload = () => {
    setImageUpload(false);
  };

  const closeMenuOptions = () => {
    setOpenMenOptions(false);
  };

  const openMenuSettingsDialog = () => {
    setOpenMenuSettings(true);
  };

  const closeMenuSettings = () => {
    setOpenMenuSettings(false);
  };

  const handleItemDelete = () => {
    confirm({ description: "You are about to delete this item!" })
      .then(() => {
        props.deleteItem(menuItem.id);
      })
      .catch(() => {
        /* ... */
      });
  };

  const openMenuItemMove = () => {
    setOpenMove(true);
    
  }
  const handleMoveClose = () => {
    setOpenMove(false);
  };

  const handleMoveToTop = () => {
    if (props.moveItemToTop) {
      props.moveItemToTop(index);
    }
  };

  const itemImage = () => {
    // console.log('menuItem', menuItem, menuImages);
    const index = menuImages.findIndex(
      (image) => image.menuItemId === menuItem.id
    );
    if (index !== -1) {
      return menuImages[index].imageUrl;
    }

    if (menuItem.image) {
      return menuItem.image;
    }
    return null;
  };

  const showWeekDays = (activeDays) => {
    let weekdays = "";
    if (activeDays && activeDays.length > 0) {
      weekdays = `on ${activeDays.join(",")}`;
    }
    return weekdays;
  };

  const hideWeekDays = (hideDays) => {
    let weekdays = "";
    if (hideDays && hideDays.length > 0) {
      weekdays = `on ${hideDays.join(",")}`;
    }
    return weekdays;
  };

  const handleSizeDelete = (e, chipToDelete) => {
    console.log("handleSizeDelete");
    const updatedItemIndex = menuItem.sizes.findIndex(
      (item) => item.id === chipToDelete.id
    );
    if (updatedItemIndex !== -1) {
      const newSizeItems = removeItemWithSlice(
        menuItem.sizes,
        updatedItemIndex
      );
      const newItem = { ...menuItem, sizes: newSizeItems };
      updateTheMenuItem(newItem, categories);
    }
  };

  const moveSize = () => {};

  const handleItemEdit = () => {
    props.editItem(true);
  };

  const handleSizeGroupDrop = (index, item) => {
    if (!item || !item.item) {
      return;
    }
    //console.log('category handledrop', index, item, category.items);
    let size = menuItem.sizes[index];
    //console.log('menuItem', menuItem);
    if (size.groups) {
      let newGroups = [...size.groups];
      if (newGroups.filter((group) => group.id === item.item.id).length > 0) {
        return;
      }
      newGroups.push({ id: item.item.id, sort: 999 });
      size.groups = newGroups;
    } else {
      size.groups = [{ id: item.item.id, sort: 999 }];
    }
    let newSizes = [...menuItem.sizes];
    newSizes[index] = size;
    updateTheMenuItem({ ...menuItem, sizes: newSizes }, categories);
  };

  const handleMenuItemGroupDrop = (index, item) => {
    if (!item || !item.item) {
      return;
    }
    let newItem = { ...menuItem };

    //console.log('menuItem', menuItem);
    let newGroups;
    if (newItem.groups) {
      newGroups = [...menuItem.groups];
      if (newGroups.filter((group) => group.id === item.item.id).length > 0) {
        return;
      }
      newGroups.push({ id: item.item.id, sort: 999 });
    } else {
      newGroups = [{ id: item.item.id, sort: 999 }];
    }
    updateTheMenuItem({ ...menuItem, groups: newGroups }, categories);
  };

  return (
    <Card className={classes.root}>
      <CardContent>
        <Grid
          container
          directions="column"
          alignContent="space-between"
          spacing={2}
        >
          <Grid
            item
            container
            direction="row"
            xs={12}
            alignContent="space-between"
            spacing={1}
          >
            <Grid item>
              <Grid
                className={classes.placeholder}
                item
                container
                direction="column"
              >
                {itemImage() ? (
                  <>
                    <img
                      src={itemImage()}
                      alt="image"
                      height={100}
                      width={100}
                    />
                    <Grid item container direction="row">
                      <IconButton
                        aria-label="more"
                        className={classes.margin}
                        edge="end"
                        color="secondary"
                        onClick={deleteImage}
                      >
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>

                      <IconButton
                        aria-label="more"
                        className={classes.margin}
                        edge="end"
                        color="secondary"
                        onClick={openImageUpload}
                      >
                        <CloudUploadIcon fontSize="small" />
                      </IconButton>
                    </Grid>
                  </>
                ) : (
                  <IconButton
                    aria-label="more"
                    className={classes.margin}
                    edge="end"
                    color="secondary"
                    onClick={openImageUpload}
                  >
                    <CloudUploadIcon fontSize="small" />
                  </IconButton>
                )}
              </Grid>
              <MenuItemImage
                open={imageUpload}
                handImageClose={closeImageUpload}
                menuId={menuId}
                categoryId={categoryId}
                menuItemId={menuItem.id}
              />
            </Grid>

            <Grid item xs sx={{ minWidth: 0 }}>
              <Grid className={classes.margin}>
                <Typography
                  component="h3"
                  variant="subtitle1"
                  sx={{ fontWeight: 600, overflowWrap: "anywhere", wordBreak: "break-word" }}
                >
                  {menuItem.displayNumber ? (
                    <Typography component="span" variant="body2" color="textSecondary" sx={{ mr: 0.75 }}>
                      {menuItem.displayNumber}
                    </Typography>
                  ) : null}
                  {menuItem.colorHex ? (
                    <span className="selected-color" style={{ backgroundColor: menuItem.colorHex }}>
                      {menuItem.name}
                    </span>
                  ) : menuItem.name}
                  <Typography component="span" sx={{ fontWeight: 600, ml: 1 }}>
                    {formatPrice(menuItem.price)}
                  </Typography>
                  {menuItem.gstFree === true ? (
                    <Typography component="span" variant="caption" color="textSecondary"> (GST Free)</Typography>
                  ) : ""}
                </Typography>
                {showIds ? (
                <Box mt={0.5} mb={0.5}>
                  <Tooltip title="Item ID — click to select">
                    <Chip
                      size="small"
                      icon={<TagIcon style={{ fontSize: '0.75rem' }} />}
                      label={menuItem.id}
                      className={classes.idChip}
                    />
                  </Tooltip>
                </Box>
                ) : null}
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                >
                  {menuItem.altName}
                </Typography>
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  sx={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
                >
                  {menuItem.description}
                </Typography>
              </Grid>
              <Grid
                item
                container
                direction="row"
                spacing={1}
                justifyContent="space-between"
                className={classes.margin}
              >
                {menuItem.tags && menuItem.tags.length > 0
                  ? menuItem.tags.map((tag) => {
                      return (
                        <Chip
                          size="small"
                          icon={<LabelIcon />}
                          key={tag}
                          label={tag}
                          style={{ backgroundColor: teal[300] }}
                          className={classes.marginRightBottom}
                        />
                      );
                    })
                  : null}
                {liveOffShelf ? (
                  <Tooltip
                    title={
                      liveOffShelf.reason === OFF_SHELF_SOLD_OUT
                        ? "Stock ran out, so the dish was taken off automatically"
                        : "Taken off by hand from the POS"
                    }
                  >
                    <Chip
                      size="small"
                      icon={<BlockIcon fontSize="small" />}
                      label={offShelfLabel(liveOffShelf)}
                      style={{
                        backgroundColor:
                          liveOffShelf.reason === OFF_SHELF_SOLD_OUT ? red[200] : deepOrange[200],
                      }}
                      className={classes.marginRightBottom}
                    />
                  </Tooltip>
                ) : null}
                {!menuItem.active ? (
                  <Chip
                    size="small"
                    label={`Hidden from menu ${hideWeekDays(
                      menuItem.hideDays
                    )}`}
                    style={{ backgroundColor: yellow[700] }}
                    className={classes.marginRightBottom}
                  />
                ) : null}
                {menuItem.hidden_until ? (
                  <Chip
                    size="small"
                    label={`Hidden Util ${new Date(
                      menuItem.hidden_until
                    ).toLocaleString()}`}
                    style={{ backgroundColor: yellow[700] }}
                    className={classes.marginRightBottom}
                  />
                ) : null}

                {menuItem.active_begin && menuItem.active_end ? (
                  <Chip
                    size="small"
                    label={`Show only from ${minutesToString(
                      menuItem.active_begin
                    )} - ${minutesToString(menuItem.active_end)} ${showWeekDays(
                      menuItem.activeWeekDays
                    )}`}
                    style={{ backgroundColor: yellow[700] }}
                    className={classes.marginRightBottom}
                  />
                ) : null}
                {menuItem.cookingCategory ? (
                  <Chip
                    size="small"
                    icon={<TabletIcon />}
                    label={menuItem.cookingCategory}
                    style={{ backgroundColor: lime[500] }}
                    className={classes.marginRightBottom}
                  />
                ) : null}

                {menuItem.printCategory
                  ? menuItem.printCategory.split(";").map((category) => {
                      return (
                        <Chip
                          size="small"
                          icon={<PrintIcon />}
                          key={category}
                          label={category}
                          style={{ backgroundColor: deepPurple[200] }}
                          className={classes.marginRightBottom}
                        />
                      );
                    })
                  : null}

                {menuItem.isTakeawayDisabled ? (
                  <Chip
                    size="small"
                    icon={<BlockIcon />}
                    key={"isTakeawayDisabled"}
                    label={"Takeaway"}
                    style={{ backgroundColor: grey[200] }}
                    className={classes.marginRightBottom}
                  />
                ) : null}

                {menuItem.isForPrintingDisabled ? (
                  <Chip
                    size="small"
                    icon={<BlockIcon />}
                    key={"isForPrintingDisabled"}
                    label={"Print"}
                    style={{ backgroundColor: grey[200] }}
                    className={classes.marginRightBottom}
                  />
                ) : null}

                {menuItem.isForKDSDisabled ? (
                  <Chip
                    size="small"
                    icon={<BlockIcon />}
                    key={"isForKDSDisabled"}
                    label={"KDS"}
                    style={{ backgroundColor: grey[200] }}
                    className={classes.marginRightBottom}
                  />
                ) : null}

                {menuItem.isMainCourse === true ? (
                  <Chip
                    size="small"
                    icon={<RestaurantIcon />}
                    key={"isForKDSDisabled"}
                    label={"Main"}
                    style={{ backgroundColor: red[200] }}
                    className={classes.marginRightBottom}
                  />
                ) : null}
                {menuItem.promotionTags && menuItem.promotionTags.length > 0 ? (
                  
                  menuItem.promotionTags.map((tag) => {
                    return (
                      <Chip
                        size="small"
                        key={tag}
                        label={tag}
                        style={{ backgroundColor: teal[300] }}
                        className={classes.marginRightBottom}
                      />
                    );
                  }
                  )
                ) : null}
              </Grid>
            </Grid>
            <Grid item xs={12} sm="auto" sx={{ maxWidth: "100%" }}>
              <MenuItemOptions
                open={openMenOptions}
                handClose={closeMenuOptions}
                menuId={menuId}
                categoryId={categoryId}
                menuItem={menuItem}
              />
              <MenuMiscSettings
                open={openMenuSettings}
                handClose={closeMenuSettings}
                menuItem={menuItem}
                menuId={menuId}
                categoryId={categoryId}
              />

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                  maxWidth: 200,
                }}
              >
                <Tooltip title="Dish settings">
                  <IconButton
                    aria-label="dish settings"
                    size="small"
                    color="secondary"
                    onClick={openMenuSettingsDialog}
                  >
                    <SettingsOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Visibility and options">
                  <IconButton
                    aria-label="dish options"
                    size="small"
                    color="secondary"
                    onClick={openMenuOptions}
                  >
                    <MoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Edit dish">
                  <IconButton
                    aria-label="edit dish"
                    size="small"
                    color="secondary"
                    onClick={handleItemEdit}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Delete dish">
                  <IconButton
                    aria-label="delete dish"
                    size="small"
                    color="secondary"
                    onClick={handleItemDelete}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Move to another category">
                  <IconButton
                    aria-label="move dish"
                    size="small"
                    color="secondary"
                    onClick={openMenuItemMove}
                  >
                    <MoveToInboxIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Move to top of category">
                  <IconButton
                    aria-label="move to top"
                    size="small"
                    color="primary"
                    onClick={handleMoveToTop}
                  >
                    <VerticalAlignTopIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>

              <MoveCategoryDialog
              open={openMove}
              closeHandler={handleMoveClose}
              selectedMenuItem={menuItem}
             
            />
              
            </Grid>
          </Grid>
        </Grid>
        {sizeList.length > 0 ? (
          <Grid item container direction="column">
            {/* <Paper variant="outlined" className={classes.paper}> */}

            <Typography variant="overline" color="textSecondary">
              Sizes
            </Typography>
            {sizeList.map((data, index) => {
              let icon;
              return (
                data && (
                  <TargetBox
                    accept={["size", "group"]}
                    dragType={"size"}
                    key={data.id}
                    index={index}
                    id={data.id}
                    onDrop={(item) => handleSizeGroupDrop(index, item)}
                    moveCard={moveSize}
                  >
                    <Box key={data.id} className={clsx(classes.sizeClass, classes.sizeRow)}>
                      <Typography variant="body2">{data.name}</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatPrice(data.price)}
                      </Typography>
                      {data.groups &&
                        data.groups.map((group) => {
                          return (
                            <Chip
                              key={group.id}
                              label={getGroupName(group.id)}
                            />
                          );
                        })}
                      <Tooltip title="Delete size">
                        <IconButton
                          aria-label={`delete size ${data.name}`}
                          size="small"
                          sx={{ ml: "auto" }}
                          color="secondary"
                          onClick={(e) => handleSizeDelete(e, data)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TargetBox>
                )
              );
            })}
            {/* </Paper> */}
          </Grid>
        ) : null}
        <Typography variant="overline" color="textSecondary">
          Options
        </Typography>
        <TargetBox
          accept={["group"]}
          dragType={"item"}
          index={index}
          id={menuItem.id}
          onDrop={(item) => handleMenuItemGroupDrop(index, item)}
          className={classes.optionsClass}
        >
          <Grid>
            <Paper component="ul" elevation={0} className={classes.paper}>
              {groupList.length === 0 ? (
                <Typography variant="body2" className={classes.dropHint}>
                  Drag a choice group here
                </Typography>
              ) : null}
              {groupList.map((data, index) => {
                let icon;
                // console.log('data', data);
                return (
                  data && (
                    <TargetBox
                      accept={["menuGroup"]}
                      dragType={"menuGroup"}
                      key={data.id}
                      index={index}
                      id={data.id}
                      moveCard={moveGroup}
                    >
                      <li key={data.id}>
                        <GroupChip
                          icon={icon}
                          size="small"
                          label={getGroupName(data.id)}
                          onDelete={handleDelete(data)}
                          className={classes.chip}
                        />
                      </li>
                    </TargetBox>
                  )
                );
              })}
            </Paper>
          </Grid>

        </TargetBox>
      </CardContent>
    </Card>
  );
};

export default MenuItemDisplay;
